// src/features/links/NoiseNotice.tsx
// IR-1386 (Tomer): the queue now hides collection noise by default — Telegram
// personal/"Contact me" accounts, Telegram bots, and dead invite links.
//
// Hiding it SILENTLY would be worse than the problem it solves: an analyst who
// cannot see that 5,424 links were held back has no way to tell a filtered
// queue from a collection outage (IR-1371 was exactly that — WhatsApp links
// stopped arriving for 27 days and the screen looked normal). So the count is
// always on screen, and every bucket is one click from being reviewed.
import type { NoiseHidden } from "./types";

/** Which noise bucket the analyst is currently reviewing, if any. */
export type NoiseBucket = "user" | "bot" | "broken";

interface NoiseNoticeProps {
  noise: NoiseHidden | null;
  reviewing: NoiseBucket | null;
  onReview: (bucket: NoiseBucket) => void;
  onBackToQueue: () => void;
}

const LABELS: Record<NoiseBucket, { one: string; many: string }> = {
  user: { one: "personal account", many: "personal accounts" },
  bot: { one: "bot", many: "bots" },
  broken: { one: "dead link", many: "dead links" },
};

function label(bucket: NoiseBucket, n: number) {
  const l = LABELS[bucket];
  return `${n.toLocaleString()} ${n === 1 ? l.one : l.many}`;
}

export function NoiseNotice({ noise, reviewing, onReview, onBackToQueue }: NoiseNoticeProps) {
  if (reviewing) {
    return (
      <p className="text-xs text-[var(--iris-fg-3)]">
        Showing {LABELS[reviewing].many} only — these are hidden from the queue by default.{" "}
        <button
          type="button"
          className="underline hover:text-[var(--iris-fg-1)]"
          onClick={onBackToQueue}
        >
          Back to the queue
        </button>
      </p>
    );
  }

  // Backend not deployed yet, or genuinely nothing hidden — say nothing rather
  // than claim "0 hidden", which reads as a broken counter.
  if (!noise || noise.total === 0) return null;

  const buckets = (["user", "bot", "broken"] as const).filter((b) => noise[b] > 0);

  return (
    <p className="text-xs text-[var(--iris-fg-3)]">
      <strong className="text-[var(--iris-fg-1)]">{noise.total.toLocaleString()}</strong> links
      hidden as noise — nothing was dismissed:{" "}
      {buckets.map((b, i) => (
        <span key={b}>
          {i > 0 && " · "}
          <button
            type="button"
            className="underline hover:text-[var(--iris-fg-1)]"
            onClick={() => onReview(b)}
          >
            {label(b, noise[b])}
          </button>
        </span>
      ))}
    </p>
  );
}
